import { ListSkeleton } from '@atlas/ui-components'
import { useParams } from 'react-router-dom'
import { CampaignForm } from '@/components/campaigns/CampaignForm'
import { PageHeader } from '@/components/editorial/PageHeader'
import type { Campaign } from '@/domain/campaign.types'
import { useCampaign } from '@/hooks/useCampaigns'
import { buildUpdateCampaignInput, campaignFormValuesFrom, toComboboxOptions } from '@/pages/Campaigns/form/form-model'
import { UnsavedChangesDialog } from '@/pages/Campaigns/form/UnsavedChangesDialog'
import { useCampaignFormContainer } from '@/pages/Campaigns/form/useCampaignFormContainer'
import { useCampaignFormState } from '@/pages/Campaigns/form/useCampaignFormState'
import { useUnsavedNavigationGuard } from '@/pages/Campaigns/form/useUnsavedNavigationGuard'

/**
 * The form, mounted only once the catalog has resolved.
 *
 * `useCampaignFormState` seeds from its argument on the FIRST render and ignores later
 * ones, so the catalog has to be present by then — the stored brand-axis code cannot be
 * placed in the Brand or the Product field without knowing which level it names. Mounting
 * early and letting the set fill in afterwards silently files every product as a brand.
 */
function EditCampaignForm({ campaign }: { campaign: Campaign }) {
	const formContainer = useCampaignFormContainer()
	const productCodes = new Set((formContainer.product?.options ?? []).map((option) => option.code))
	const form = useCampaignFormState(campaignFormValuesFrom(campaign, productCodes))
	const guard = useUnsavedNavigationGuard(form.isDirty)

	function handleSubmit() {
		if (!form.validate()) return
		formContainer.updateMutation.mutate(
			{ campaignId: campaign.campaignId, input: buildUpdateCampaignInput(form.values, form.touched) },
			{ onSuccess: () => guard.navigateAfterSave(`/campaigns/${campaign.campaignId}`) },
		)
	}

	return (
		<>
			<CampaignForm
				values={form.values}
				fieldErrors={form.fieldErrors}
				isDirty={form.isDirty}
				isPending={formContainer.updateMutation.isPending}
				generalError={formContainer.updateMutation.isError ? 'Something went wrong saving this Campaign.' : undefined}
				isSuccess={formContainer.updateMutation.isSuccess}
				campaignCode={campaign.campaignCode}
				objectiveOptions={toComboboxOptions(formContainer.objective)}
				geographyOptions={toComboboxOptions(formContainer.geography)}
				brandOptions={toComboboxOptions(formContainer.brand)}
				productOptions={toComboboxOptions(formContainer.product)}
				onFieldChange={form.onFieldChange}
				onSubmit={handleSubmit}
				onCancel={() => guard.onCancel(`/campaigns/${campaign.campaignId}`)}
			/>
			<UnsavedChangesDialog
				isOpen={guard.isWarningOpen}
				onDiscard={guard.onDiscard}
				onKeepEditing={guard.onKeepEditing}
			/>
		</>
	)
}

/**
 * Holds the form back until the classification catalog has loaded.
 *
 * Its own component so the wait is a mount condition rather than a prop: the form seeds
 * its state once, and seeding it without the catalog puts every product in the Brand
 * field. Nothing here re-reads — the catalog query is shared and already in flight.
 */
function EditCampaignFormGate({ campaign }: { campaign: Campaign }) {
	const { isCatalogLoading } = useCampaignFormContainer()
	if (isCatalogLoading) return <ListSkeleton label="Loading…" />
	return <EditCampaignForm campaign={campaign} />
}

/** Edit Campaign — hosts the shared form pre-filled from the detail query, with the immutable campaign code read-only. */
export function EditCampaignPage() {
	const { campaignId } = useParams<{ campaignId: string }>()
	const detail = useCampaign(campaignId ?? '')

	return (
		<div>
			<PageHeader eyebrow="Campaigns" title="Edit Campaign" />
			{detail.isLoading && <ListSkeleton label="Loading…" />}
			{detail.isError && (
				<p role="alert" className="text-sm text-ds-danger">
					Unable to load this Campaign.
				</p>
			)}
			{detail.data && <EditCampaignFormGate campaign={detail.data} />}
		</div>
	)
}
